import { useMemo, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { COUNTRIES } from '@wlb/shared/constants/countries';
import { PROFILE_INFO_LIMITS } from '@wlb/shared/api/onboarding/onboardingErrors';
import {
  HEIGHT_UNIT,
  fromHeightCM,
  fromHeightMeters,
  toHeightCM,
  toHeightMeters,
} from '@wlb/shared/utils/heightUtils';
import { WEIGHT_UNIT, fromWeightKg, toWeightKg } from '@wlb/shared/utils/weightUtils';
import { toDateOnlyString, parseDateOnlyString } from '@wlb/shared/utils/dateHelpers';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { BrandLogo } from '../components/ui/BrandLogo';
import { FormError } from '../components/ui/FormError';
import { ScaleBar } from '../components/ui/ScaleBar';
import { ScrollDatePicker } from '../components/ui/ScrollDatePicker';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import { MAX_DOB_YEAR, MIN_DOB_YEAR } from '../features/auth/dobCalendar';
import type { OnboardingLocationState, OnboardingProfileState } from '../features/auth/onboardingFlowState';

type ActiveControl = 'height' | 'weight' | 'dob' | null;

const DEFAULT_DOB = new Date(2005, 0, 25);

const dobFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const inputClass = (error?: string): string =>
  `auth-flow__input${error ? ' auth-flow__input--error' : ''}`;

/** Minimum age requirement for the platform. */
const MIN_AGE = PROFILE_INFO_LIMITS.MIN_AGE_YEARS;
const { min: MIN_HEIGHT_CM, max: MAX_HEIGHT_CM } = PROFILE_INFO_LIMITS.HEIGHT_CM;

const heightUnitKey = (unit: string): string =>
  unit === HEIGHT_UNIT.FEET ? 'basicInfo.unitShortFeet' : 'basicInfo.unitShortMeters';

const weightUnitKey = (unit: string): string =>
  unit === WEIGHT_UNIT.KG ? 'basicInfo.unitShortKg' : 'basicInfo.unitShortLbs';

/** Trims conversion noise so a limit reads as "3 m" / "9.84 ft". */
const round2 = (value: number): number => Number(value.toFixed(2));

type FieldName = 'gender' | 'country' | 'height' | 'weight' | 'dob';

type FieldErrors = Partial<Record<FieldName, string>>;

/**
 * A failed rule plus the values needed to phrase it. Kept free of `t` so the
 * limits can be expressed in whichever unit the user picked.
 */
interface FieldIssue {
  key: string;
  values?: Record<string, string | number>;
  /** Translation key for the unit appended to `values.max`. */
  unitKey?: string;
}

interface ProfileValues {
  countryId: number;
  height: string;
  heightUnit: string;
  weight: string;
  weightUnit: string;
  dob: Date;
}

/**
 * Applies the rules the API enforces, but against the units on screen: the
 * request carries centimetres, so the bound is checked after conversion and
 * reported back in the unit the user is typing in.
 */
const collectIssues = (values: ProfileValues): Partial<Record<FieldName, FieldIssue>> => {
  const issues: Partial<Record<FieldName, FieldIssue>> = {};

  if (!values.countryId) issues.country = { key: 'basicInfo.errors.countryRequired' };

  const heightNum = Number(values.height);
  if (!values.height || isNaN(heightNum) || heightNum <= 0) {
    issues.height = { key: 'basicInfo.errors.heightInvalid' };
  } else {
    const heightCM = toHeightCM(heightNum, values.heightUnit);
    if (heightCM <= MIN_HEIGHT_CM || heightCM > MAX_HEIGHT_CM) {
      issues.height = {
        key: 'basicInfo.errors.heightOutOfRange',
        values: { max: round2(fromHeightCM(MAX_HEIGHT_CM, values.heightUnit)) },
        unitKey: heightUnitKey(values.heightUnit),
      };
    }
  }

  const weightNum = Number(values.weight);
  if (!values.weight || isNaN(weightNum) || weightNum <= 0) {
    issues.weight = { key: 'basicInfo.errors.weightInvalid' };
  }

  const now = new Date();
  const minDob = new Date(now.getFullYear() - MIN_AGE, now.getMonth(), now.getDate());
  if (values.dob > now || values.dob > minDob) {
    issues.dob = { key: 'basicInfo.errors.dobInvalid', values: { age: MIN_AGE } };
  }

  return issues;
};

export function BasicInfo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-fill from router state when navigating back from /upload-image, either
  // through its Back button or because the API rejected one of these answers.
  const flowState = location.state as OnboardingLocationState | null;
  const incoming = flowState?.profile;
  const rejected = flowState?.fieldErrors;

  /** Phrases an issue with its limit already converted to the unit on screen. */
  const describe = (issue?: FieldIssue): string | undefined =>
    issue && t(issue.key, { ...issue.values, unit: issue.unitKey ? t(issue.unitKey) : '' });

  const messagesFor = (values: ProfileValues): FieldErrors => {
    const issues = collectIssues(values);
    return {
      country: describe(issues.country),
      height: describe(issues.height),
      weight: describe(issues.weight),
      dob: describe(issues.dob),
    };
  };

  /**
   * The API states its bounds in centimetres and kilograms. Where our own rules
   * catch the same field, that wording wins because it is already in the unit
   * the user typed in; anything else keeps the server's text.
   */
  const seedErrors = (values: ProfileValues): FieldErrors => {
    if (!rejected) return {};
    const local = messagesFor(values);
    const prefer = (server?: string, own?: string): string | undefined =>
      server ? own ?? server : undefined;

    return {
      gender: rejected.gender,
      country: prefer(rejected.country, local.country),
      height: prefer(rejected.height, local.height),
      weight: prefer(rejected.weight, local.weight),
      dob: prefer(rejected.dob, local.dob),
    };
  };

  const initial: ProfileValues = {
    countryId: incoming?.countryId ?? 0,
    heightUnit: incoming?.heightUnit ?? HEIGHT_UNIT.METERS,
    height: incoming
      ? String(round2(fromHeightMeters(incoming.heightMetres, incoming.heightUnit)))
      : '1.45',
    weightUnit: incoming?.weightUnit ?? WEIGHT_UNIT.LBS,
    weight: incoming ? String(incoming.weight) : '135',
    dob: incoming
      ? parseDateOnlyString(incoming.dob) ?? new Date(incoming.dob)
      : DEFAULT_DOB,
  };

  const [gender, setGender] = useState(incoming?.gender ?? 'Male');
  const [countryId, setCountryId] = useState(initial.countryId);
  const [height, setHeight] = useState(initial.height);
  const [weight, setWeight] = useState(initial.weight);
  const [dob, setDob] = useState<Date>(initial.dob);
  const [activeControl, setActiveControl] = useState<ActiveControl>(null);
  const [heightUnit, setHeightUnit] = useState(initial.heightUnit);
  const [weightUnit, setWeightUnit] = useState(initial.weightUnit);
  const [errors, setErrors] = useState<FieldErrors>(() => seedErrors(initial));
  // The banner explains why the user was brought back here; the fields carry
  // the reason.
  const [serverRejected, setServerRejected] = useState(Boolean(rejected));

  const dobLabel = dobFormat.format(dob);
  const heightUnitLabel = t(heightUnitKey(heightUnit));
  const weightUnitLabel = t(weightUnitKey(weightUnit));

  /** Earliest and latest selectable dates for the scroll picker. */
  const minDob = useMemo(() => new Date(MIN_DOB_YEAR, 0, 1), []);
  const maxDob = useMemo(() => {
    const d = new Date();
    return new Date(MAX_DOB_YEAR, d.getMonth(), d.getDate());
  }, []);

  const openDobPicker = () => setActiveControl('dob');

  /** Drops a message as soon as its field is edited so it cannot go stale. */
  const clearError = (field: FieldName) =>
    setErrors(current => (current[field] ? { ...current, [field]: undefined } : current));

  /** Re-expresses the entered value in the newly picked unit. */
  const switchHeightUnit = (next: string) => {
    setHeight(value =>
      value ? String(round2(fromHeightMeters(toHeightMeters(Number(value), heightUnit), next))) : value,
    );
    setHeightUnit(next);
    clearError('height');
  };

  const switchWeightUnit = (next: string) => {
    setWeight(value =>
      value ? String(round2(fromWeightKg(toWeightKg(Number(value), weightUnit), next))) : value,
    );
    setWeightUnit(next);
    clearError('weight');
  };

  /**
   * BasicInfo has no API of its own: the answers travel to /upload-image and are
   * persisted there in one multipart request together with the avatar.
   */
  const goToUpload = (): boolean => {
    const errs = messagesFor({ countryId, height, heightUnit, weight, weightUnit, dob });
    setErrors(errs);
    setServerRejected(false);
    if (Object.values(errs).some(Boolean)) return false;

    const heightMetres = toHeightMeters(Number(height), heightUnit);
    const profile: OnboardingProfileState = {
      gender,
      countryId,
      heightMetres,
      heightUnit,
      weight: Number(weight),
      weightUnit,
      dob: toDateOnlyString(dob),
    };
    const state: OnboardingLocationState = { profile };
    navigate('/upload-image', { state });
    return true;
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    goToUpload();
  };

  return (
    <AuthFlowLayout>
      <div className="auth-flow__card">
        <div className="auth-flow__progress" aria-hidden><span /></div>
        <button className="auth-flow__skip" type="button" onClick={goToUpload}>{t('survey.welcome.skip')}</button>
        <BrandLogo compact className="auth-flow__brand" />
        <h1 className="auth-flow__heading">{t('basicInfo.heading')}</h1>
        <form className="auth-flow__form" onSubmit={submit} noValidate>
          <FormError variant="banner" message={serverRejected && t('basicInfo.errors.saveFailed')} />
          <fieldset
            className="auth-flow__choice-field"
            aria-describedby={errors.gender ? 'basic-info-gender-error' : undefined}
          >
            <legend>{t('basicInfo.genderLabel')}</legend>
            <div className="auth-flow__choices">
              {[
                ['Male', 'genderMale'],
                ['Female', 'genderFemale'],
                ['Other', 'genderOther'],
              ].map(([value, key]) => (
                <button key={value} type="button" className={`auth-flow__choice${gender === value ? ' auth-flow__choice--selected' : ''}`} onClick={() => { setGender(value); setActiveControl(null); clearError('gender'); }}>
                  {t(`basicInfo.${key}`)}
                </button>
              ))}
            </div>
            <FormError message={errors.gender} id="basic-info-gender-error" />
          </fieldset>

          <label className="auth-flow__field">
            <span>{t('basicInfo.countryLabel')}</span>
            <select
              className={inputClass(errors.country)}
              value={countryId ? String(countryId) : ''}
              aria-invalid={Boolean(errors.country)}
              aria-describedby={errors.country ? 'basic-info-country-error' : undefined}
              onFocus={() => setActiveControl(null)}
              onChange={event => {
                setCountryId(event.target.value ? Number(event.target.value) : 0);
                clearError('country');
              }}
            >
              <option value="">{t('basicInfo.countryPlaceholder')}</option>
              {COUNTRIES.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
            <FormError message={errors.country} id="basic-info-country-error" />
          </label>

          <label className="auth-flow__field auth-flow__interactive-field">
            <span>{t('basicInfo.heightLabel')}</span>
            <input
              className={inputClass(errors.height)}
              value={`${height} ${heightUnitLabel}`}
              aria-invalid={Boolean(errors.height)}
              aria-describedby={errors.height ? 'basic-info-height-error' : undefined}
              onFocus={() => setActiveControl('height')}
              onChange={event => { setHeight(event.target.value.replace(/[^0-9.]/g, '')); clearError('height'); }}
              placeholder={t('basicInfo.heightPlaceholder')}
            />
            <FormError message={errors.height} id="basic-info-height-error" />
          </label>

          <label className="auth-flow__field auth-flow__interactive-field">
            <span>{t('basicInfo.weightLabel')}</span>
            <input
              className={inputClass(errors.weight)}
              value={`${weight} ${weightUnitLabel}`}
              aria-invalid={Boolean(errors.weight)}
              aria-describedby={errors.weight ? 'basic-info-weight-error' : undefined}
              onFocus={() => setActiveControl('weight')}
              onChange={event => { setWeight(event.target.value.replace(/[^0-9.]/g, '')); clearError('weight'); }}
              placeholder={t('basicInfo.weightPlaceholder')}
            />
            <FormError message={errors.weight} id="basic-info-weight-error" />
          </label>

          <label className="auth-flow__field auth-flow__interactive-field">
            <span>{t('basicInfo.dobLabel')}</span>
            <input
              className={inputClass(errors.dob)}
              value={dobLabel}
              readOnly
              aria-invalid={Boolean(errors.dob)}
              aria-describedby={errors.dob ? 'basic-info-dob-error' : undefined}
              onFocus={openDobPicker}
              onClick={openDobPicker}
            />
            <FormError message={errors.dob} id="basic-info-dob-error" />
          </label>

          {activeControl === 'height' && (
            <div className="auth-flow__scale-panel">
              <div className="auth-flow__unit-toggle"><button type="button" className={heightUnit === HEIGHT_UNIT.FEET ? 'active' : ''} onClick={() => switchHeightUnit(HEIGHT_UNIT.FEET)}>{t('basicInfo.unitFeet')}</button><button type="button" className={heightUnit === HEIGHT_UNIT.METERS ? 'active' : ''} onClick={() => switchHeightUnit(HEIGHT_UNIT.METERS)}>{t('basicInfo.unitMeters')}</button></div>
              <strong>{height} {heightUnitLabel}</strong>
              <ScaleBar
                ariaLabel={t('basicInfo.heightLabel')}
                min={heightUnit === HEIGHT_UNIT.METERS ? 1.2 : 3.94}
                max={heightUnit === HEIGHT_UNIT.METERS ? 2.2 : 7.22}
                step={0.01}
                value={Number(height)}
                onChange={next => { setHeight(String(next)); clearError('height'); }}
                showValues={false}
                ticks={heightUnit === HEIGHT_UNIT.METERS ? ['1.20', '1.40', '1.60', '2.20'] : ['3.94', '4.59', '5.25', '7.22']}
              />
            </div>
          )}
          {activeControl === 'weight' && (
            <div className="auth-flow__scale-panel">
              <div className="auth-flow__unit-toggle"><button type="button" className={weightUnit === WEIGHT_UNIT.KG ? 'active' : ''} onClick={() => switchWeightUnit(WEIGHT_UNIT.KG)}>{t('basicInfo.unitKg')}</button><button type="button" className={weightUnit === WEIGHT_UNIT.LBS ? 'active' : ''} onClick={() => switchWeightUnit(WEIGHT_UNIT.LBS)}>{t('basicInfo.unitLbs')}</button></div>
              <strong>{Number(weight).toFixed(1)} {weightUnitLabel}</strong>
              <ScaleBar
                ariaLabel={t('basicInfo.weightLabel')}
                min={40}
                max={weightUnit === WEIGHT_UNIT.KG ? 150 : 330}
                value={Number(weight)}
                onChange={next => { setWeight(String(next)); clearError('weight'); }}
                showValues={false}
                ticks={weightUnit === WEIGHT_UNIT.KG ? ['40', '80', '110', '150'] : ['40', '110', '240', '330']}
              />
            </div>
          )}
          {activeControl === 'dob' && (
            <div className="auth-flow__calendar">
              <ScrollDatePicker
                value={dob}
                min={minDob}
                max={maxDob}
                ariaLabel={t('basicInfo.dobLabel')}
                onChange={next => {
                  setDob(next);
                  clearError('dob');
                }}
              />
            </div>
          )}
          <button className="auth-flow__primary" type="submit">{t('common.buttons.next')}</button>
        </form>
      </div>
    </AuthFlowLayout>
  );
}
